<template>
  <d-modal
    @close="handleClose"
    size="lg">
    <d-modal-header>
      <d-modal-title>
        {{ $t('settlementsAddPopup.title') }}
      </d-modal-title>
    </d-modal-header>
    <d-modal-body>
      <d-form class="settlement-add-form">
        <validation-observer
          ref="settlement-add-form"
          tag="form">
          <div class="form-group">
            <label for="name">
              {{ $t('settlementsAddPopup.customer') }}:
            </label>

            <div :class="{
              'custom-validation': true,
              'is-invalid': errors.indexOf('customer_id') > -1
            }">
              <select-customer
                :customerID="customerID"
                :isInPopup="true" />

              <div class="invalid-feedback">
                {{ $t('ui.thisFieldIsRequired') }}
              </div>
            </div>
          </div>

          <d-row>
            <d-col cols="8">
              <div class="form-group">
                <label for="name">
                  {{ $t('settlementsAddPopup.order') }}:
                </label>

                <div :class="{
                  'custom-validation': true,
                  'is-invalid': errors.indexOf('order_id') > -1
                }">
                  <select-order
                    ref="orderSelect"
                    :orderID="orderID"
                    :customerID="null" />
                  <div class="invalid-feedback">
                    {{ $t('ui.thisFieldIsRequired') }}
                  </div>
                </div>
              </div>
            </d-col>

            <d-col cols="4">
              <div class="form-group">
                <label for="name">
                  {{ $t('settlementsAddPopup.number') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="number"
                  v-slot="{ errors }">
                  <d-input
                    v-model="number"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>
          </d-row>

          <d-row>
            <d-col cols="3">
              <div class="form-group">
                <label for="name">
                  {{ $t('settlementsAddPopup.currency') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="currency"
                  v-slot="{ errors }">
                  <d-form-select
                    v-model="currency"
                    :state="errors && errors.length ? 'invalid' : null"
                    :options="currencies.map(currency => ({ text: currency.code, value: currency.code }))" />

                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="3">
              <div class="form-group">
                <label for="name">
                  {{ $t('settlementsAddPopup.amount') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="amount"
                  v-slot="{ errors }">
                  <input
                    :class="{
                      'form-control': true,
                      'is-invalid': errors.length
                    }"
                    :invalid="errors.length ? true : null"
                    type="number"
                    v-model="amount" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="3">
              <div class="form-group">
                <label for="name">
                  {{ $t('settlementsAddPopup.amountNet') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="amount_net"
                  v-slot="{ errors }">
                  <input
                    :class="{
                      'form-control': true,
                      'is-invalid': errors.length
                    }"
                    :invalid="errors.length ? true : null"
                    type="number"
                    v-model="amount_net" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="3">
              <div class="form-group">
                <label for="name">
                  {{ $t('settlementsAddPopup.amountPaid') }}:
                </label>

                <input
                  :class="{
                    'form-control': true
                  }"
                  type="number"
                  v-model="amount_paid" />
              </div>
            </d-col>
          </d-row>

          <d-row>
            <d-col cols="4">
              <div class="form-group">
                <label for="name">
                  {{ $t('settlementsAddPopup.invoiceDate') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="invoice_date"
                  v-slot="{ errors }">
                  <d-datepicker
                    v-model="invoice_date"
                    :monday-first="true"
                    format="yyyy-MM-dd"
                    :placeholder="$t('ui.chooseDate')"
                    :input-class="{ 'form-control': true, 'is-invalid': errors.length }"
                    :language="translationsForDatePicker" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="4">
              <div class="form-group">
                <label for="name">
                  {{ $t('settlementsAddPopup.paymentDate') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="payment_date"
                  v-slot="{ errors }">
                  <d-datepicker
                    v-model="payment_date"
                    :monday-first="true"
                    format="yyyy-MM-dd"
                    :placeholder="$t('ui.chooseDate')"
                    :input-class="{ 'form-control': true, 'is-invalid': errors.length }"
                    :language="translationsForDatePicker" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="4">
              <div class="form-group">
                <label for="name">
                  {{ $t('settlementsAddPopup.settlementDate') }}:
                </label>

                <d-datepicker
                  v-model="settlement_date"
                  :monday-first="true"
                  format="yyyy-MM-dd"
                  :placeholder="$t('ui.chooseDate')"
                  :input-class="{ 'form-control': true }"
                  :language="translationsForDatePicker" />
              </div>
            </d-col>
          </d-row>

          <d-row>
            <d-col cols="4">
              <div class="form-group">
                <label for="name">
                  {{ $t('settlementsAddPopup.status') }}:
                </label>

                <d-form-select
                  v-model="status"
                  :options="[{
                    text: $t('settlementsAddPopup.statuses.PAID'),
                    value: 'PAID'
                  }, {
                    text: $t('settlementsAddPopup.statuses.UNPAID'),
                    value: 'UNPAID'
                  }]" />
              </div>
            </d-col>

            <d-col cols="8">
              <div class="form-group">
                <label for="name">
                  {{ $t('settlementsAddPopup.filePDF') }}:
                </label>

                <div :class="{
                  'custom-validation': true,
                  'is-invalid': errors.indexOf('file') > -1
                }">
                  <file-input
                    :onChange="setNewFile"
                    id="inputFileImport" />
                  <div class="invalid-feedback">
                    {{ $t('ui.thisFieldIsRequired') }}
                  </div>
                </div>
              </div>
            </d-col>
          </d-row>
        </validation-observer>
      </d-form>
    </d-modal-body>
    <d-modal-footer>
      <d-button
        class="ml-0"
        theme="danger"
        :disabled="addInProgress"
        @click.prevent.stop="cancel">
        {{ $t('ui.cancel') }}
      </d-button>

      <d-checkbox
        inline
        v-model="send_email"
        toggle
        toggle-small
        class="ml-auto mt-1">
        {{ $t('settlements.sendEmail') }}
      </d-checkbox>

      <d-checkbox
        v-if="send_email"
        inline
        v-model="send_to_customer"
        class="ml-1 mt-1">
        {{ $t('settlements.sendEmailToCustomer') }}
      </d-checkbox>

      <d-checkbox
        v-if="send_email"
        inline
        v-model="send_to_user"
        class="ml-1 mt-1">
        {{ $t('settlements.sendEmailToUser') }}
      </d-checkbox>

      <d-button
        class="ml-2"
        theme="success"
        :disabled="addInProgress"
        @click.prevent.stop="validateForm">
        <div
          v-if="addInProgress"
          class="spinner-border spinner-border-sm"
          role="status"></div>
        {{ $t('settlements.addSettlement') }}
      </d-button>
    </d-modal-footer>
  </d-modal>
</template>

<script>
import datepickerTranslations from './../../data/datepicker-translations.js';
import SelectCustomer from './../../components/common/SelectCustomer.vue';
import SelectOrder from './../../components/common/SelectOrder.vue';
import FormUtils from './../../utils/FormUtils.js';
import jsonToFormData from 'json-form-data';
import Vue from 'vue';
import getPrefferedLanguage from './../../utils/i18nUtils.js';

export default {
  name: 'settlements-add-popup',
  props: {
    customerID: {
      default: null
    },
    orderID: {
      default: null
    }
  },
  components: {
    'select-customer': SelectCustomer,
    'select-order': SelectOrder
  },
  computed: {
    translationsForDatePicker () {
      return datepickerTranslations[getPrefferedLanguage()];
    }
  },
  data () {
    return {
      amount: '',
      amount_net: '',
      amount_paid: '',
      customer_id: null,
      currency: null,
      file: null,
      status: 'UNPAID',
      invoice_date: null,
      payment_date: null,
      settlement_date: null,
      number: '',
      order_id: '',
      send_email: false,
      send_to_customer: true,
      send_to_user: true,
      // internals
      addInProgress: false,
      currencies: [],
      loadedCurrencies: false,
      loadError: false,
      errors: []
    };
  },
  mounted () {
    this.file = null;
    this.importInProgress = false;
    this.loadAdditionalData();

    if (this.$refs['orderSelect']) {
      this.$refs['orderSelect'].loadOrders();
    }

    this.$bus.$on('ui-select-customer-updated-in-popup', this.setCustomerID);
    this.$bus.$on('ui-select-order-updated', this.setOrderID);
  },
  methods: {
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError',
        successAction: () => {
          if (this.currency === null) {
            this.currency = this.currencies[0].code;
          }
        }
      });
    },
    handleClose () {
      this.$bus.$emit('close-settlement-add-popup');
    },
    validateForm () {
      this.errors = [];

      if (!this.order_id) {
        this.errors.push('order_id');
      }

      if (!this.customer_id) {
        this.errors.push('customer_id');
      }

      if (!this.file) {
        this.errors.push('file');
      }

      FormUtils.validate(this.$refs['settlement-add-form'], this.save);
    },
    setNewFile (event) {
      if (this.errors.indexOf('file') > -1) {
        let pos = this.errors.indexOf('file');
        this.errors.splice(pos, 1);
      }

      this.file = event.target.files[0];
    },
    setCustomerID (customerID) {
      if (this.errors.indexOf('customer_id') > -1) {
        let pos = this.errors.indexOf('customer_id');
        this.errors.splice(pos, 1);
      }

      this.customer_id = customerID;
    },
    setOrderID (orderID) {
      if (this.errors.indexOf('order_id') > -1) {
        let pos = this.errors.indexOf('order_id');
        this.errors.splice(pos, 1);
      }

      this.order_id = orderID;
    },
    save () {
      if (this.errors.length) {
        return;
      }

      this.addInProgress = true;
      let formData = {
        customer_id: this.customer_id,
        order_id: this.order_id,
        number: this.number,
        amount: this.amount,
        amount_net: this.amount_net,
        amount_paid: this.amount_paid,
        currency: this.currency,
        invoice_date: this.invoice_date ? [this.invoice_date.getFullYear(), this.invoice_date.getMonth() + 1, this.invoice_date.getDate()].map(n => n < 10 ? '0' + n : n).join('-') : null,
        payment_date: this.payment_date ? [this.payment_date.getFullYear(), this.payment_date.getMonth() + 1, this.payment_date.getDate()].map(n => n < 10 ? '0' + n : n).join('-') : null,
        settlement_date: this.settlement_date ? [this.settlement_date.getFullYear(), this.settlement_date.getMonth() + 1, this.settlement_date.getDate()].map(n => n < 10 ? '0' + n : n).join('-') : null,
        status: this.status,
        file: this.file,
        send_email: +this.send_email,
      };

      if (this.send_email) {
        formData.send_to_customer = +this.send_to_customer;
        formData.send_to_user = +this.send_to_user;
      }

      this.$http.request({
        method: 'POST',
        url: '/settlements/store',
        headers: {
          'Content-Type': undefined
        },
        transformRequest (data) {
          let dataToSend = jsonToFormData(data);
          return dataToSend;
        },
        data: formData
      }).then(() => {
        this.cancel();
        this.addInProgress = false;

        Vue.swal({
          title: this.$t('settlementsAddPopup.addTitle'),
          html: this.$t('settlementsAddPopup.addDesc'),
          type: 'success',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        this.$bus.$emit('settlements-reload-needed');
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccured'),
          html: this.$t('ui.errorOccuredDesc'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.addInProgress = false;
      });
    },
    cancel () {
      this.handleClose();
    }
  },
  beforeDestroy () {
    this.$bus.$off('ui-select-customer-updated-in-popup', this.setCustomerID);
    this.$bus.$off('ui-select-order-updated', this.setOrderID);
  }
}
</script>

<style lang="scss">
.settlement-add-form {
  .custom-validation.is-invalid {
    & > .invalid-feedback {
      display: block;
    }

    .is-select {
      border-color: #c4183c;
    }
  }
}
</style>
